import AbstractApiService from 'src/services/abstractApiService';
import { api, getAuthorizationToken } from 'src/pages/APIs';
import { onApiRegionChange, whenRegionReady } from 'src/pages/api-base-url';
import { getOrgId } from 'src/utils/organization/org_id_helper';
import { ref } from 'vue';
import type { dynamicObj } from 'src/pages/assets';
import { userApiService } from '..';

export default class OperatorApiService extends AbstractApiService {
  private ws: WebSocket | null = null;
  private isConnected = false;
  private reconnectInterval = 3000; // Reconnect every 3 seconds
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10; // Limit reconnect attempts to prevent infinite loops
  private onMessageCallback: ((message: any) => void) | null = null;
  private regionListenerInstalled = false;

  public status = ref<string>('ready');

  public constructor() {
    super({});
  }

  private getWsUrl(): string {
    return api?.op_ws_chat || 'ws://localhost:8020/api/v1/run-task';
  }

  public async get_token(): Promise<string> {
    try {
      const get_token = await getAuthorizationToken(); // Replace with your actual token retrieval method
      const string_token = JSON.stringify(get_token);
      const raw_token = string_token.replace('Bearer ', '');
      const formatted_token = raw_token.replace(/^"|"$/g, ''); // Removes leading & trailing quotes
      return formatted_token;
    } catch (error) {
      logInfo('Error getting authorization token:', error);
      throw error; // Re-throw to handle in the component
    }
  }

  public async connect(): Promise<void> {
    if (this.isConnected || this.ws) {
      // Do not connect if already connected or if there is an existing socket
      return;
    }

    await whenRegionReady();

    if (!this.regionListenerInstalled) {
      this.regionListenerInstalled = true;
      let lastRegion: string | null = null;
      onApiRegionChange((region) => {
        if (lastRegion === null) {
          lastRegion = region;
          return;
        }
        if (lastRegion === region) return;
        lastRegion = region;
        logInfo('[Operator WS] Region changed, tearing down socket', { region });
        this.disconnect();
      });
    }

    try {
      this.ws = new WebSocket(this.getWsUrl());

      this.ws.onopen = () => {
        logInfo('[WebSocket] Connection opened');
        this.isConnected = true;
        this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
        this.status.value = 'ready'; // Update status when the connection is open
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          this.status.value = data.status;

          if (this.onMessageCallback) {
            this.onMessageCallback(data); // Pass the parsed data to the callback
          }
        } catch (error) {
          logInfo('[WebSocket] Error parsing message:', error);
        }
      };

      this.ws.onclose = () => {
        console.log('[WebSocket] Connection closed');
        this.isConnected = false;
        this.ws = null;
        this.status.value = 'Disconnected';
        this.reconnect(); // Reconnect on close
      };

      this.ws.onerror = (error: Event) => {
        logInfo('[WebSocket] Error:', error);
      };
    } catch (error) {
      logInfo('Error creating WebSocket:', error);
    }
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
    }
  }

  private reconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      logInfo(
        `Attempting to reconnect WebSocket in ${this.reconnectInterval / 1000} seconds (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})...`,
      );
      setTimeout(() => {
        this.connect();
      }, this.reconnectInterval);
    } else {
      logError('Maximum WebSocket reconnect attempts reached.');
    }
  }

  public onMessage(callback: (message: any) => void): void {
    this.onMessageCallback = callback;
  }

  public async getApiKey(): Promise<string> {
    try {
      const data = {
        app_key: 'FELLOW_DOC2',
      };
      const response = await userApiService.getAppSubscription(data);
      return response[0].token as string;
    } catch (error) {
      logError('Error getting api key:', error);
      throw error;
    }
  }

  async submitChatMessage(
    message: string,
    task_id: string,
    token: string,
    action: string,
    sensitive_data: any,
    active_tool: string,
  ): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      logWarn('WebSocket not connected or not open. Attempting to reconnect.');
      if (!this.isConnected) {
        this.connect();
        return Promise.resolve();
      } else {
        return Promise.resolve();
      }
    }

    const apiKey = await this.getApiKey();
    const payload = {
      task: message,
      task_id: task_id,
      token: token,
      action: action,
      api_key: apiKey,
      org_id: getOrgId(),
      sensitive_data: sensitive_data,
      use_tool: active_tool,
    };
    try {
      this.ws?.send(JSON.stringify(payload));
      this.status.value = 'Running'; //Set as running when we send the message
      logInfo('Sending WebSocket message:', payload);
    } catch (error) {
      logInfo('Error sending WebSocket message:', error);
      this.status.value = 'Error';
    }

    return Promise.resolve();
  }

  public async savePrompt(dataJson: any): Promise<any> {
    return super.post(api.save_prompt + '?org_id=' + getOrgId(), dataJson);
  }

  public async getAllPrompts(separate: boolean): Promise<any> {
    const request_params = {} as dynamicObj;
    request_params['org_id'] = getOrgId();
    request_params['separate'] = separate;
    const querystring = this.encodeQueryData(request_params);
    return super.get(api.get_all_prompts + '?' + querystring, {});
  }

  public async deletePrompt(prompt_id: string): Promise<any> {
    const request_params = {} as dynamicObj;
    request_params['org_id'] = getOrgId();
    request_params['prompt_id'] = prompt_id;
    const querystring = this.encodeQueryData(request_params);
    return super.delete(api.delete_prompt + '?' + querystring, {});
  }

  public async downloadOperatorFile(
    fileName: string,
    task_id: string,
  ): Promise<any> {
    const request_params = {} as dynamicObj;
    request_params['file_name'] = fileName;
    request_params['org_id'] = getOrgId();
    request_params['task_id'] = task_id;
    const querystring = this.encodeQueryData(request_params);
    return super.get(api.download_operator_files + '?' + querystring, {
      responseType: 'blob',
    });
  }

  public async getDocNetTools(): Promise<any> {
    return super.get(api.get_docnet_tools);
  }
}
